import { motion } from 'framer-motion'
import { Camera, CheckCircle, AlertTriangle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { PhotoItem } from '../../types'
import { APP_NAME, BRAND, CONSULTATION_URL } from '../../config'
import LogoIcon from '../../assets/landing/logo-icon.svg'
import {
  HeaderMetaChip,
  ReportSectionCard,
  ReportSectionHeader,
  ReportSectionBody,
} from './ReportSectionCard'

function scoreTheme(score: number): {
  accent: string
  badge: string
  label: string
} {
  if (score >= 8) {
    return {
      accent: '#00a63e',
      badge: 'bg-[#dcfce7] text-[#008236]',
      label: 'text-[#008236]',
    }
  }
  if (score >= 6) {
    return {
      accent: '#d08700',
      badge: 'bg-[#fef3c6] text-[#a65f00]',
      label: 'text-[#a65f00]',
    }
  }
  return {
    accent: '#ef042d',
    badge: 'bg-[#ffe2e2] text-[#c10007]',
    label: 'text-[#c10007]',
  }
}

function issueBadgeClass(issue: string): string {
  const lower = issue.toLowerCase()
  if (lower.includes('light') || lower.includes('blur') || lower.includes('dark')) {
    return 'bg-[#fef3c6] text-[#a65f00]'
  }
  if (lower.includes('composition') || lower.includes('angle') || lower.includes('crop')) {
    return 'bg-[#dbeafe] text-[#1447e6]'
  }
  return 'bg-[#ffedd4] text-[#ca3500]'
}

function PhotoIssueCard({ photo, index }: { photo: PhotoItem; index: number }) {
  const score = photo.ai_feedback?.quality_score ?? 0
  const theme = scoreTheme(score)
  const category = photo.category?.replace(/_/g, ' ') ?? 'Photo'

  return (
    <motion.article
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 + index * 0.06 }}
      className="relative rounded-[14px] bg-[#f2f2f6] p-[18px]"
    >
      <div className="flex items-start gap-3.5">
        <a
          href={photo.url}
          target="_blank"
          rel="noopener noreferrer"
          className="group relative block h-[88px] w-[88px] shrink-0 overflow-hidden rounded-[12px] bg-white ring-1 ring-[#e8e8ec] transition-opacity hover:opacity-90"
        >
          <img
            src={photo.thumbnail_url || photo.url}
            alt={category}
            className="h-full w-full object-cover"
            loading="lazy"
            onError={e => {
              ;(e.target as HTMLImageElement).style.display = 'none'
            }}
          />
          {photo.ai_feedback && (
            <span
              className={`absolute bottom-1 end-1 rounded-[4px] px-1.5 py-0.5 text-[10px] font-bold tabular-nums ${theme.badge}`}
            >
              {photo.ai_feedback.quality_score}/10
            </span>
          )}
        </a>

        <div className="min-w-0 flex-1 pt-0.5">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[1.2px] text-[#99a1af]">
              {category}
            </span>
            {photo.ai_feedback?.issues?.map((issue, j) => (
              <span
                key={j}
                className={`rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize ${issueBadgeClass(issue)}`}
              >
                {issue}
              </span>
            ))}
          </div>

          {photo.ai_feedback?.suggestion && (
            <div className="flex items-start gap-2.5">
              <AlertTriangle className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${theme.label}`} />
              <p className="text-xs leading-5 text-[#4a5565]">
                {photo.ai_feedback.suggestion}
              </p>
            </div>
          )}
        </div>
      </div>
    </motion.article>
  )
}

function PhotosCallout() {
  const { t } = useTranslation('report')

  return (
    <div
      className="flex items-start gap-3 rounded-[14px] border px-4 py-4"
      style={{ borderColor: `${BRAND.red}30`, backgroundColor: `${BRAND.red}08` }}
    >
      <img src={LogoIcon} alt="logo" className="h-7 w-7 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="mb-1 text-xs font-bold text-[#01114f]">
          {t('sectionCallout.title', { appName: APP_NAME })}
        </p>
        <p className="mb-2 text-xs leading-relaxed text-[#4a5565]">
          {t('callout.photos.message', { appName: APP_NAME })}
        </p>
        <a
          href={CONSULTATION_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs font-bold text-[#ef042d] transition-opacity hover:opacity-90"
        >
          {t('callout.photos.cta')}
        </a>
      </div>
    </div>
  )
}

interface PhotosSectionProps {
  photos: PhotoItem[]
  totalCount: number
}

export function PhotosSection({ photos, totalCount }: PhotosSectionProps) {
  const { t } = useTranslation('report')
  const hasAIFeedback = photos.some(p => p.ai_feedback != null)
  const photosToShow = hasAIFeedback
    ? photos.filter(p => p.ai_feedback?.needs_improvement === true)
    : photos

  if (hasAIFeedback && photosToShow.length === 0) {
    return (
      <ReportSectionCard delay={0.23}>
        <ReportSectionHeader
          icon={Camera}
          accentColor="#d08700"
          title={t('photos.titleGood')}
          right={<HeaderMetaChip>{t('photos.total', { total: totalCount })}</HeaderMetaChip>}
        />
        <ReportSectionBody>
          <div className="flex items-center gap-3 rounded-[14px] border border-[#b9f8cf] bg-[#f0fdf4] px-4 py-3.5">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white">
              <CheckCircle className="h-4 w-4 text-[#00a63e]" strokeWidth={2} />
            </div>
            <p className="text-sm leading-snug text-[#008236]">
              {t('photos.allGood', { total: totalCount })}
            </p>
          </div>
        </ReportSectionBody>
      </ReportSectionCard>
    )
  }

  return (
    <ReportSectionCard delay={0.23}>
      <ReportSectionHeader
        icon={Camera}
        accentColor="#d08700"
        title={t('photos.titleIssues')}
        subtitle={hasAIFeedback ? t('photos.aiReviewed') : undefined}
        right={
          <HeaderMetaChip>
            {t('photos.total', { total: totalCount })} · {t('photos.flagged', { flagged: photosToShow.length })}
          </HeaderMetaChip>
        }
      />

      <ReportSectionBody className="space-y-5">
        <div className="grid grid-cols-1 gap-[18px] lg:grid-cols-2">
        {photosToShow.map((photo, i) => (
          <PhotoIssueCard key={photo.external_id || String(i)} photo={photo} index={i} />
        ))}
        </div>

        {photosToShow.length > 0 && <PhotosCallout />}
      </ReportSectionBody>
    </ReportSectionCard>
  )
}
